<template>
  <StyledOddsButton
    v-if="hasSelections"
    ref="returnsEl"
    :class="[classes.returns]"
    :href="href"
    :meta="meta"
    :odds="returnsAmount"
    :winnings="returnsAmount"
    :buttonId="returnsButtonId"
    @click="handleClick"
  />
  <StyledIconButton
    v-else
    ref="emptyPicksEl"
    :meta="meta"
    :buttonId="emptyPicksButtonId"
  >
    <template #default="{ color }">
      <Picks
        width="15"
        height="15"
        :color="color"
        :class="[classes.picksIcon]"
      />
    </template>
  </StyledIconButton>
</template>

<script lang="ts">
import {
  injectAdTag,
  injectBrand
} from "@fresh8/ad-data-composition/src/brand";
import { getTranslation } from "@fresh8/ad-data-composition/src/lang";
import { injectParlayStates } from "@fresh8/ad-data-composition/src/parlayStates";
import { composePopoverForm } from "@fresh8/ad-data-composition/src/popoverForm";
import { createUseStyles } from "@fresh8/vue-jss/src";
import { computed, defineComponent, provide } from "vue";
import Picks from "../../icons/Picks.vue";
import StyledOddsButton from "../../styled/OddsButton.vue";
import StyledIconButton from "./styled/IconButton.vue";
import { useStake } from "@fresh8/ad-data-composition/src/usePotentialWinnings";
import formatCurrency from "@fresh8/ad-data-composition/src/lib/formatCurrency";
import { injectCardNumber, useAnalytics } from "../../../hooks/useAnalytics";
import { injectBetData } from "@fresh8/ad-data-composition/src/betData";
import {
  acca,
  generate
} from "@fresh8/ad-data-composition/src/lib/generateLinks";

const useStyles = createUseStyles({
  returns: {
    flex: "1",
    background: "#142E5D",
    borderRadius: "17px",
    color: "white"
  },
  picksIcon: {
    height: "15px",
    width: "15px"
  }
});

export default defineComponent({
  name: "ReturnButton",
  components: {
    Picks,
    StyledOddsButton,
    StyledIconButton
  },
  props: {
    hasPicks: { type: Boolean, required: false, default: false }
  },
  setup(props) {
    const adTag = injectAdTag();
    const { adTemplate, brand, potentialWinnings } = injectBrand();
    const { stakeAmount } = useStake();
    const {
      hasSelections: hasParlaySelections,
      parlayAccumulator,
      getSelections
    } = injectParlayStates();
    const betData = injectBetData();

    provide("button-transition-disable", true);

    const { targetEl: returnsEl } = composePopoverForm("Returns Button", [
      {
        type: "button",
        get: () => brand.value.adTemplate.market.oddsButton,
        set: (value: string) =>
          (brand.value.adTemplate.market.oddsButton = value)
      }
    ]);

    const { targetEl: emptyPicksEl } = composePopoverForm("Picks Button", [
      {
        type: "button",
        get: () => brand.value.adTemplate.parlayCard?.emptyPicksButton || "",
        set: (value: string) => {
          if (brand.value.adTemplate.parlayCard)
            brand.value.adTemplate.parlayCard.emptyPicksButton = value;
        }
      }
    ]);

    const returnsButtonId = computed(() => adTemplate.value.market.oddsButton);
    const emptyPicksButtonId = computed(
      () => adTemplate.value.parlayCard?.emptyPicksButton
    );
    const translations = getTranslation(
      window.navigator.language.toLowerCase()
    );
    const hasSelections = computed(
      () => props.hasPicks || hasParlaySelections()
    );
    const meta = computed(() =>
      hasSelections.value
        ? translations.ParlayTexts.Return
        : translations.ParlayTexts.Picks
    );
    const returnsAmount = computed(() => {
      if (potentialWinnings.value)
        return formatCurrency(
          potentialWinnings.value.currency,
          Math.round(stakeAmount.value * parlayAccumulator.value),
          false
        );

      return "";
    });

    const cardNumber = injectCardNumber();

    const { handleClick: handleAnalytics } = useAnalytics(
      { ...props, cardNumber },
      "Parlay Button"
    );

    const handleClick = () => {
      handleAnalytics(getSelections.value);
    };

    const href = computed(() => {
      const operatorKey = betData?.operator.value?.name || "";
      const stake = stakeAmount.value;
      const affiliateId = betData?.affiliateId.value || "";
      const targetUrl = encodeURIComponent(betData?.targetUrl.value || "");
      const options = betData?.options.value;
      const clickUrl = betData?.clickUrl.value;

      const campaignTarget = adTag?.selectedCampaign?.value?.targetUrl;
      const { fallbackUrl, targetUrlType } =
        adTag?.adTag?.value?.tracking[
          adTag.selectedOperator?.value?._id as string
        ] || {};
      if (targetUrlType && targetUrlType === "direct") {
        if (campaignTarget && campaignTarget !== "") {
          return generate(campaignTarget, {
            affiliateId: betData?.affiliateId.value,
            clickUrl: betData?.clickUrl.value,
            operatorKey: betData?.operator.value?.name || ""
          });
        } else if (fallbackUrl && fallbackUrl !== "") {
          return generate(fallbackUrl, {
            affiliateId: betData?.affiliateId.value,
            clickUrl: betData?.clickUrl.value,
            operatorKey: betData?.operator.value?.name || ""
          });
        }
        return betData?.links.value.registration;
      }

      return acca({
        operatorKey,
        stake,
        affiliateId,
        targetUrl,
        getSelections,
        options,
        clickUrl
      });
    });

    return {
      meta,
      returnsAmount,
      href,
      returnsButtonId,
      emptyPicksButtonId,
      classes: useStyles(),
      returnsEl,
      emptyPicksEl,
      hasSelections,
      handleClick
    };
  }
});
</script>
